/**
 * BaseNoteCard Component
 *
 * Composable base component for displaying notes with slot-based customization.
 * Follows MediaItem pattern with overlay slots for actions and badges.
 */

import React from "react";
import { NoteDisplay } from "./NoteDisplay";
import { TagBadge, TimestampBadge } from "./NoteBadges";

export interface NoteData {
  id: string;
  content: string;
  tags: string[];
  status?: string;
  createdAt?: string;
}

interface BaseNoteCardProps {
  note: NoteData;
  variant?: "default" | "pending" | "compact" | "flat";
  showTimestamp?: boolean;
  topRight?: React.ReactNode;
  bottomRight?: React.ReactNode;
  bottomLeft?: React.ReactNode;
  className?: string;
  tagVariant?: "default" | "amber" | "violet" | "blue";
  children?: React.ReactNode;
}

const variantStyles: Record<string, string> = {
  default: "bg-white border border-gray-200",
  pending: "bg-amber-50 border border-amber-200",
  compact: "bg-gray-50 border border-gray-200",
  flat: "",
};

export const BaseNoteCard: React.FC<BaseNoteCardProps> = ({
  note,
  variant = "default",
  showTimestamp = true,
  topRight,
  bottomRight,
  bottomLeft,
  className = "",
  tagVariant = "default",
  children,
}) => {
  const baseStyles = variantStyles[variant];

  return (
    <div
      className={`${variant === "flat" ? "py-3" : "rounded-lg p-3"} ${baseStyles} ${className}`}
    >
      {/* Header row with actions */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <NoteDisplay content={note.content} />
        </div>
        {topRight && <div className="flex items-center gap-2 ml-2">{topRight}</div>}
      </div>

      {/* Tags row */}
      {note.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {note.tags.map((tag, idx) => (
            <TagBadge key={idx} tag={tag} variant={tagVariant} />
          ))}
        </div>
      )}

      {/* Footer row with timestamp and actions */}
      {(showTimestamp && note.createdAt) || bottomLeft || bottomRight ? (
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            {showTimestamp && note.createdAt && (
              <TimestampBadge date={note.createdAt} />
            )}
            {bottomLeft}
          </div>
          {bottomRight && <div>{bottomRight}</div>}
        </div>
      ) : null}

      {/* Additional content slot */}
      {children}
    </div>
  );
};
